import React from 'react';
import {
  withStyles,
  WithStyles,
  Theme,
  createStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import LeadDetailsForm from './LeadDetailsForm';
import OtpForm from './OtpForm';
import SignupInvite from './SignupInvite';
import { LeadDetails } from './adsGenerator.types';

export type LeadDialogStep = 'details' | 'otp' | 'signup';

const TITLES: Record<LeadDialogStep, { title: string; subtitle: string }> = {
  details: {
    title: 'Almost there!',
    subtitle: 'Share your details and we will send your ads on WhatsApp.',
  },
  otp: {
    title: 'Verify your number',
    subtitle: 'Verify your number to see your ads.',
  },
  signup: {
    title: "You've used your free generation",
    subtitle: 'Join AiSensy to create more ads for your business.',
  },
};

const LeadCaptureDialog = (props: Props): React.ReactElement => {
  const {
    classes,
    open,
    step,
    details,
    onDetailsChange,
    onSendOtp,
    otp,
    onOtpChange,
    onVerifyOtp,
    onResendOtp,
    onEditDetails,
    onClose,
    isSubmitting,
    isResending,
    error,
    signupUrl,
    otpChannels,
    verifiedPhone,
  } = props;

  // After a refresh the form is empty, so fall back to the session's phone.
  const phoneLabel = details.phoneNumber
    ? `${details.countryCode} ${details.phoneNumber}`
    : verifiedPhone
      ? `+${verifiedPhone}`
      : '';
  const heading = TITLES[step];
  // Details and OTP can't be dismissed: generation is already running and only verifying shows the ads.
  const closable = step === 'signup';

  return (
    <Dialog
      open={open}
      onClose={closable && !isSubmitting ? onClose : undefined}
      maxWidth="xs"
      fullWidth
      disableBackdropClick
      aria-labelledby="lead-capture-title"
    >
      <DialogTitle id="lead-capture-title" disableTypography>
        <Typography variant="h3" className={classes.title}>
          {heading.title}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {heading.subtitle}
        </Typography>
        {closable ? (
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onClose}
            disabled={isSubmitting}
          >
            <Close />
          </IconButton>
        ) : null}
      </DialogTitle>
      <DialogContent className={classes.content}>
        {step === 'details' ? (
          <LeadDetailsForm
            value={details}
            onChange={onDetailsChange}
            onSubmit={onSendOtp}
            isSubmitting={isSubmitting}
            error={error}
          />
        ) : step === 'signup' ? (
          <SignupInvite
            signupUrl={signupUrl}
            onUseAnotherNumber={onEditDetails}
          />
        ) : (
          <OtpForm
            phoneLabel={phoneLabel}
            otpChannels={otpChannels}
            otp={otp}
            onOtpChange={onOtpChange}
            onSubmit={onVerifyOtp}
            onResend={onResendOtp}
            onEditDetails={onEditDetails}
            isSubmitting={isSubmitting}
            isResending={isResending}
            error={error}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

interface Props extends WithStyles<typeof styles> {
  open: boolean;
  step: LeadDialogStep;
  details: LeadDetails;
  onDetailsChange: (value: LeadDetails) => void;
  onSendOtp: () => void;
  otp: string;
  onOtpChange: (otp: string) => void;
  onVerifyOtp: () => void;
  onResendOtp: () => void;
  onEditDetails: () => void;
  onClose: () => void;
  isSubmitting: boolean;
  isResending: boolean;
  error: string;
  signupUrl: string;
  otpChannels: string[];
  verifiedPhone?: string;
}

const styles = (theme: Theme) =>
  createStyles({
    title: {
      fontWeight: 600,
      paddingRight: theme.spacing(4),
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
    },
    content: {
      paddingBottom: theme.spacing(3),
    },
  });

export default withStyles(styles)(LeadCaptureDialog);
